import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { GraduationCap, CheckCircle, ArrowRight } from "lucide-react";

const TrainingService = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <GraduationCap className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Training & Awareness</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Empower your team with security knowledge. Build a security-conscious culture.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Building Security Awareness</h2>
              <p className="mb-6">
                Human error remains one of the leading causes of security incidents, responsible for over 80% of data breaches. Our training and awareness programs equip your team with the knowledge and skills to recognize and respond to security threats, creating a strong human firewall for your organisation.
              </p>

              <h2 className="text-2xl font-bold mb-4">Program Structure</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <ul className="space-y-2">
                  <li><strong>Monthly awareness campaigns:</strong> Themed content, posters, and bite-sized learning</li>
                  <li><strong>Quarterly role-based training:</strong> Targeted sessions for different job functions</li>
                  <li><strong>Annual phishing simulations:</strong> Real-world testing with personalized coaching</li>
                  <li><strong>Ongoing reinforcement:</strong> Micro-learning videos and gamified challenges</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Training Modules</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Phishing & social engineering</strong> (30 min) - Recognizing and reporting suspicious emails and calls</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Password security</strong> (20 min) - Creating strong passwords and using password managers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Data handling & classification</strong> (45 min) - Protecting sensitive information</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Incident reporting</strong> (15 min) - What to do when something goes wrong</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Secure coding practices</strong> (90 min) - For developers: OWASP Top 10 and secure SDLC</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Executive security briefings</strong> (60 min) - Leadership perspective on cyber risk</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Delivery Methods</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">In-Person Workshops</p>
                  <p className="text-sm text-muted-foreground">Interactive sessions with hands-on exercises and group discussions</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Interactive E-Learning</p>
                  <p className="text-sm text-muted-foreground">Self-paced modules with quizzes and practical scenarios</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Micro-Learning Videos</p>
                  <p className="text-sm text-muted-foreground">2-3 minute videos on specific topics for quick learning</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Gamified Challenges</p>
                  <p className="text-sm text-muted-foreground">Points, badges, and leaderboards to drive engagement</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">Proven Effectiveness</h2>
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <p className="mb-3">Our training programs deliver measurable results:</p>
                <ul className="space-y-2">
                  <li>• <strong>70% reduction</strong> in phishing click rates after 6 months</li>
                  <li>• <strong>85% improvement</strong> in security incident reporting rates</li>
                  <li>• <strong>90% completion rates</strong> for awareness training modules</li>
                  <li>• <strong>60% fewer</strong> password-related helpdesk tickets</li>
                  <li>• <strong>Sustained behavior change</strong> with ongoing reinforcement</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Compliance Training</h2>
              <p className="mb-4">We provide specialized training for:</p>
              <ul className="space-y-2">
                <li>• <strong>GDPR awareness</strong> for all staff handling personal data</li>
                <li>• <strong>Industry-specific requirements</strong> (PCI-DSS, HIPAA, FCA)</li>
                <li>• <strong>Role-based security training</strong> for developers, administrators, and executives</li>
                <li>• <strong>Certification preparation</strong> supporting Cyber Essentials and ISO 27001</li>
              </ul>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to strengthen your security culture?</h3>
              <p className="text-muted-foreground mb-6">
                Let's create a training program that empowers your team to be your first line of defense.
              </p>
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TrainingService;
